/**
 * =============================================================================
 * IDS CRM Web - Equipment Type Settings
 * =============================================================================
 *
 * PURPOSE:
 * Manager+ editing of the configurable equipment type list (the Equipment
 * registry's type dropdown and the equipment targeting dimension on check
 * form templates). Backed by the equipment_types key in support_settings via
 * GET/PUT /check-form-templates/equipment-types.
 *
 * Each type carries a check frequency the demand engine uses:
 *   daily         - once per engineer per day (van)
 *   per_job       - once per job when the kit is selected on it (toolbox)
 *   daily_per_job - each day a job holding the kit takes work logs (hire kit)
 *   none          - registered only, never demands a check
 *
 * Type VALUES are fixed once created (templates and equipment rows reference
 * them by value); labels and frequencies are freely editable and types
 * deactivate rather than delete when still in use.
 *
 * =============================================================================
 */

import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const slugify = (text) => String(text || '').trim().toLowerCase()
  .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const CHECK_FREQUENCIES = [
  { value: 'none', label: 'No check required' },
  { value: 'daily', label: 'Daily (once per engineer per day)' },
  { value: 'per_job', label: 'Per job (when selected on a job)' },
  { value: 'daily_per_job', label: 'Daily per job (each day a job runs)' }
];

function EquipmentTypeSettings() {
  const { authFetch, user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await authFetch('/api/v1/check-form-templates/equipment-types');
      if (!response.ok) throw new Error('Failed to load equipment types');
      const data = await response.json();
      setItems((data.data || []).map(item => ({ ...item, active: item.active !== false })));
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load equipment types' });
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const handleToggle = () => {
    if (!expanded && items === null) load();
    setExpanded(prev => !prev);
  };

  const setItem = (index, patch) => {
    setItems(prev => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const handleAdd = () => {
    const label = newLabel.trim();
    const value = slugify(label);
    if (!label || !value) return;
    if ((items || []).some(item => item.value === value)) {
      setMessage({ type: 'error', text: `An equipment type with the code '${value}' already exists` });
      return;
    }
    setItems(prev => [...(prev || []), { value, label, check_frequency: 'per_job', active: true, isNew: true }]);
    setNewLabel('');
    setMessage(null);
  };

  const handleRemove = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const response = await authFetch('/api/v1/check-form-templates/equipment-types', {
        method: 'PUT',
        body: JSON.stringify({
          types: (items || []).map(({ value, label, check_frequency, active }) => ({ value, label, check_frequency, active }))
        })
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage({ type: 'error', text: body.message || 'Failed to save equipment types' });
        return;
      }
      setItems((body.data || []).map(item => ({ ...item, active: item.active !== false })));
      setMessage({ type: 'success', text: 'Equipment types saved' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save equipment types' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="coverage-matrix-section sales-category-section">
      <div className="coverage-header">
        <h3>Equipment Types</h3>
        <button type="button" className="btn-secondary" onClick={handleToggle} disabled={loading}>
          {loading ? 'Loading...' : expanded ? 'Hide Equipment Types' : 'Manage Equipment Types'}
        </button>
      </div>

      {expanded && items !== null && (
        <div className="sales-category-editor">
          <p className="section-hint">
            These types drive the Equipment registry dropdown (Settings &rarr; Equipment) and
            the equipment targeting on check form templates. Add a type here (e.g. Ladder),
            then author a check form targeting it. Deactivate a type to hide it from new
            equipment without affecting existing records; types still in use cannot be removed.
          </p>

          <table className="sales-category-table">
            <thead>
              <tr>
                <th>Label</th>
                <th>Code</th>
                <th>Check Frequency</th>
                <th>Active</th>
                {canEdit && <th />}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.value} className={item.active ? '' : 'inactive-row'}>
                  <td>
                    <input
                      type="text"
                      value={item.label}
                      disabled={!canEdit}
                      onChange={(e) => setItem(index, { label: e.target.value })}
                      aria-label={`Label for ${item.value}`}
                    />
                  </td>
                  <td><code>{item.value}</code></td>
                  <td>
                    <select
                      value={item.check_frequency || 'none'}
                      disabled={!canEdit}
                      onChange={(e) => setItem(index, { check_frequency: e.target.value })}
                      aria-label={`Check frequency for ${item.value}`}
                    >
                      {CHECK_FREQUENCIES.map(freq => (
                        <option key={freq.value} value={freq.value}>{freq.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      type="checkbox"
                      checked={item.active}
                      disabled={!canEdit}
                      onChange={(e) => setItem(index, { active: e.target.checked })}
                      aria-label={`Active for ${item.value}`}
                    />
                  </td>
                  {canEdit && (
                    <td>
                      <button
                        type="button"
                        className="btn-secondary btn-small"
                        onClick={() => handleRemove(index)}
                      >
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {canEdit && (
            <div className="sales-category-add">
              <input
                type="text"
                placeholder="New equipment type label (e.g. Ladder)"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
                aria-label="New equipment type label"
              />
              <button type="button" className="btn-secondary" onClick={handleAdd} disabled={!newLabel.trim()}>
                Add Type
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Equipment Types'}
              </button>
            </div>
          )}

          {message && (
            <div className={`sales-category-message ${message.type}`}>{message.text}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default EquipmentTypeSettings;
